/** @jsxImportSource solid-js */

import {
  Component,
  createContext,
  useContext,
  createSignal,
  createMemo,
  JSXElement,
} from 'solid-js';
import {
  ISnackBarMessage,
  defineSolidConnectSnackbar,
} from '@tcweb/connect-ui';

// The web component types are already declared in @tcweb/connect-ui

// Initialize the web component
defineSolidConnectSnackbar();

interface SnackbarContextType {
  showSnackbar(message: ISnackBarMessage): void;
  hideSnackbar(): void;
}

const SnackbarContext = createContext<SnackbarContextType>();

export interface SnackbarProviderProps {
  children: JSXElement;
}

export const SnackbarProvider: Component<SnackbarProviderProps> = (props) => {
  const [currentMessage, setCurrentMessage] = createSignal<
    ISnackBarMessage | undefined
  >(undefined);

  const showSnackbar = (message: ISnackBarMessage): void => {
    // Clear the message first to ensure the effect triggers even with identical text
    // This fixes the issue where consecutive messages with the same text don't display
    setCurrentMessage(undefined);

    // Use queueMicrotask to set the new message in the next microtask
    // This ensures the text changes from undefined -> actual text, triggering the effect
    queueMicrotask(() => {
      setCurrentMessage(message);
    });
  };

  const hideSnackbar = (): void => {
    setCurrentMessage(undefined);
  };

  const contextValue: SnackbarContextType = {
    showSnackbar,
    hideSnackbar,
  };

  // Memoize the JSON string to prevent infinite re-renders
  const messageString = createMemo(() => {
    const message = currentMessage();
    const jsonString = message ? JSON.stringify(message) : undefined;
    return jsonString;
  });

  return (
    <SnackbarContext.Provider value={contextValue}>
      {props.children}
      {/* Use the web component from connect-ui */}
      {/* @ts-expect-error -- tc-connect-snackbar is a custom web component not recognized by TypeScript */}
      <tc-connect-snackbar message={messageString()} />
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

export default SnackbarProvider;
